import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { RxRequestService } from '../../../core/services/rx-request.service';
import { activateServiceConfig } from '../../api/activate-service.config';

export interface ActivateGlobalActionState {
  type: string;
  display_name: string;
  description: string;
  is_disabled: boolean;
  disabled_reason: string;
}

type ActivateGlobalActionsStatus = 'unresolved' | 'success' | 'failed';

interface ActivateGlobalActionsResponse {
  actions?: ActivateGlobalActionState[];
  data?: { actions?: ActivateGlobalActionState[] };
}

export class ActivateGlobalActionsService {
  static deps = [RxRequestService];

  private readonly actionsSubject = new BehaviorSubject<ActivateGlobalActionState[] | null>(null);

  private activeRequest?: Observable<ActivateGlobalActionState[]>;

  private cacheVersion = 0;

  private status: ActivateGlobalActionsStatus = 'unresolved';

  private readonly rxRequestService: RxRequestService;

  constructor(rxRequestService: RxRequestService) {
    this.rxRequestService = rxRequestService;
  }

  getActions(forceRefresh = false): Observable<ActivateGlobalActionState[]> {
    if (!forceRefresh && this.status !== 'unresolved') {
      return of(this.actionsSubject.getValue() ?? []);
    }

    if (forceRefresh) {
      this.cacheVersion += 1;
      this.activeRequest = undefined;
    } else if (this.activeRequest) {
      return this.activeRequest;
    }

    const requestVersion = this.cacheVersion;
    const request = this.rxRequestService
      .get<ActivateGlobalActionsResponse>(activateServiceConfig.getActionStateURL(), {
        uiOptions: {
          handleUnauthorisedResponse: false,
          showLoading: false,
          showErrorMsg: false
        }
      })
      .pipe(
        map(response => response.actions ?? response.data?.actions ?? []),
        tap(actions => {
          if (this.cacheVersion === requestVersion) {
            this.status = 'success';
            this.actionsSubject.next(actions);
          }
        }),
        catchError(error => {
          if (this.cacheVersion === requestVersion) {
            this.status = 'failed';
            this.actionsSubject.next([]);
          }
          return throwError(error);
        }),
        finalize(() => {
          if (this.cacheVersion === requestVersion) {
            this.activeRequest = undefined;
          }
        }),
        shareReplay(1)
      );

    this.activeRequest = request;
    return request;
  }

  observeActions(): Observable<ActivateGlobalActionState[] | null> {
    return this.actionsSubject.asObservable();
  }

  getActionsValue(): ActivateGlobalActionState[] | null {
    return this.actionsSubject.getValue();
  }

  getAction(actionType: string): ActivateGlobalActionState | undefined {
    return this.getActionsValue()?.find(action => action.type === actionType);
  }

  refresh(): Observable<ActivateGlobalActionState[]> {
    return this.getActions(true);
  }

  invalidate() {
    this.cacheVersion += 1;
    this.activeRequest = undefined;
    this.status = 'unresolved';
    this.actionsSubject.next(null);
  }
}
